import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import { apiRequest, getImageUrl } from "../../../core/common/api";
import AppImage from "../../../shared/AppImage";
import ToggleSwitch from "../../../shared/ToggleSwitch";

const MAX_IMAGES = 10;
const MAX_SIZE = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

type Category = {
  _id: string;
  name: string;
  isActive?: boolean;
};

type ProductForm = {
  name: string;
  isActive: boolean;
  shortDescription: string;
  description: string;
  category: string;
  subCategory: string;
  price: string;
  sellingPrice: string;
  stock: string;
  shippingCharge: string;
  allowBackorders: boolean;
  isReturnable: boolean;
  isFeatured: boolean;
  isBestSeller: boolean;
  isNewArrival: boolean;
};

type ProductImage = {
  id: string;
  file: File;
  preview: string;
};

const initialForm: ProductForm = {
  name: "",
  isActive: true,
  shortDescription: "",
  description: "",
  category: "",
  subCategory: "",
  price: "",
  sellingPrice: "",
  stock: "",
  shippingCharge: "",
  allowBackorders: false,
  isReturnable: true,
  isFeatured: false,
  isBestSeller: false,
  isNewArrival: false,
};

const ProductAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState<ProductForm>(initialForm);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [images, setImages] = useState<ProductImage[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const imagesRef = useRef<ProductImage[]>([]);
  const [saving, setSaving] = useState(false);

  const discountPercent = useMemo(() => {
    const price = Number(form.price);
    const selling = Number(form.sellingPrice);

    return price > 0 && selling > 0 && selling < price
      ? Math.round(((price - selling) / price) * 100)
      : 0;
  }, [form.price, form.sellingPrice]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const meta = await apiRequest("/products/form-meta", "GET");

        setCategories(
          (meta.categories || []).filter(
            (item: Category) => item.isActive !== false,
          ),
        );

        if (!isEditMode && meta.defaultShippingCharge != null) {
          setForm((prev) => ({
            ...prev,
            shippingCharge: String(meta.defaultShippingCharge),
          }));
        }
      } catch (error: any) {
        Swal.fire("Error", error.message, "error");
      }
    };

    loadInitialData();
  }, [isEditMode]);

  useEffect(() => {
    if (categoryId || !form.category || !categories.length) return;

    const matchingCategory = categories.find(
      (category) =>
        category.name.trim().toLowerCase() ===
        form.category.trim().toLowerCase(),
    );

    if (matchingCategory) {
      setCategoryId(matchingCategory._id);
    }
  }, [categories, categoryId, form.category]);

  useEffect(() => {
    if (!id) return;

    const fetchProductToEdit = async () => {
      try {
        const res = await apiRequest(`/products/${id}`, "GET");
        if (res.success && res.product) {
          const prod = res.product;
          setForm({
            name: prod.name || "",
            isActive: prod.isActive,
            shortDescription: prod.shortDescription || "",
            description: prod.description || "",
            category: prod.category?.name || prod.category || "",
            subCategory: prod.subCategory?.name || prod.subCategory || "",
            price: String(prod.price || ""),
            sellingPrice: String(prod.sellingPrice || ""),
            stock: String(prod.stock ?? ""),
            shippingCharge: String(prod.shippingCharge || ""),
            allowBackorders: Boolean(prod.allowBackorders),
            isReturnable: Boolean(prod.isReturnable),
            isFeatured: Boolean(prod.isFeatured),
            isBestSeller: Boolean(prod.isBestSeller),
            isNewArrival: Boolean(prod.isNewArrival),
          });

          setExistingImages(
            Array.isArray(prod.images)
              ? prod.images.filter(
                  (image: unknown): image is string =>
                    typeof image === "string" && Boolean(image),
                )
              : [],
          );

          if (prod.category?._id) {
            setCategoryId(prod.category._id);
          }
        }
      } catch (error: any) {
        Swal.fire("Error", error.message || "Failed to load product", "error");
      }
    };

    fetchProductToEdit();
  }, [id]);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
    },
    [],
  );

  const update = <K extends keyof ProductForm>(
    key: K,
    value: ProductForm[K],
  ) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const changeCategory = (id: string) => {
    const category = categories.find((item) => item._id === id);

    setCategoryId(id);

    setForm((current) => ({
      ...current,
      category: category?.name || "",
      subCategory: "",
    }));
  };

  const selectImages = (files: FileList | null) => {
    if (!files?.length) return;

    const available = MAX_IMAGES - images.length;
    const selected = Array.from(files).slice(0, available);

    const validImages: ProductImage[] = [];

    selected.forEach((file) => {
      if (!IMAGE_TYPES.includes(file.type)) {
        Swal.fire("Error", `${file.name}: Invalid image type`, "error");
        return;
      }

      if (file.size > MAX_SIZE) {
        Swal.fire("Error", `${file.name}: Maximum size is 5MB`, "error");
        return;
      }

      const id = `${file.name}-${file.size}-${file.lastModified}`;

      if (images.some((image) => image.id === id)) return;

      validImages.push({
        id,
        file,
        preview: URL.createObjectURL(file),
      });
    });

    setImages((current) => [...current, ...validImages].slice(0, MAX_IMAGES));
  };

  const removeImage = (id: string) => {
    setImages((current) => {
      const image = current.find((item) => item.id === id);

      if (image) URL.revokeObjectURL(image.preview);

      return current.filter((item) => item.id !== id);
    });
  };

  const validate = () => {
    const price = Number(form.price);
    const selling = Number(form.sellingPrice || 0);
    const stock = Number(form.stock);

    if (!form.category) return "Category is required";

    if (!Number.isFinite(price) || price < 0) {
      return "Enter a valid price";
    }

    if (selling < 0 || selling > price) {
      return "Selling price cannot exceed price";
    }

    if (!Number.isInteger(stock) || stock < 0 || stock > 9999) {
      return "Stock must be between 0 and 9999";
    }

    return "";
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = validate();

    if (error) {
      Swal.fire("Validation Error", error, "error");
      return;
    }

    setSaving(true);

    try {
      const payload = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        payload.append(key, String(value));
      });

      images.forEach(({ file }) => {
        payload.append("images", file);
      });

      const endpoint = isEditMode ? `/products/${id}` : "/products";
      const method = isEditMode ? "PUT" : "POST";

      const result = await apiRequest(endpoint, method, payload);

      if (!result.success) {
        throw new Error(
          result.message ||
            `Unable to ${isEditMode ? "update" : "create"} product`,
        );
      }

      await Swal.fire({
        title: isEditMode ? "Product Updated" : "Product Published",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/products");
    } catch (error: any) {
      Swal.fire(
        isEditMode ? "Update Failed" : "Add Product Failed",
        error.message,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="product-add-form" onSubmit={handleSubmit}>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">MANAGEMENT</div>

          <h1 className="page-title">
            Product <em>{isEditMode ? "Edit" : "Add"}</em>
          </h1>

          <div className="page-sub">
            {isEditMode
              ? "Update product details and inventory."
              : "Create and publish a new product."}
          </div>
        </div>

        <div className="page-hdr-actions">
          <button
            type="submit"
            className="btn-sm btn-primary"
            disabled={saving}
          >
            {saving
              ? isEditMode
                ? "Updating..."
                : "Publishing..."
              : isEditMode
                ? "Update Product"
                : "Publish Product"}
          </button>
        </div>
      </div>

      <section className="add-grid">
        <section className="add-card">
          <h2>Product Information</h2>

          <div className="add-form-grid">
            <Field label="Product Name" required className="span-2">
              <input
                required
                maxLength={100}
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                placeholder="Enter product name"
              />
            </Field>

            <Field label="Category" required>
              <select
                required
                value={categoryId}
                onChange={(event) => changeCategory(event.target.value)}
              >
                <option value="">Select category</option>

                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Short Description" className="span-3">
              <input
                maxLength={180}
                value={form.shortDescription}
                onChange={(event) =>
                  update("shortDescription", event.target.value)
                }
                placeholder="Small product highlight"
              />
            </Field>

            <Field label="Description" required className="span-3">
              <div className="editor-shell">
                <textarea
                  required
                  value={form.description}
                  onChange={(event) =>
                    update("description", event.target.value)
                  }
                  placeholder="Write product description..."
                />

                <small>
                  {form.description.split(/\s+/).filter(Boolean).length} words
                </small>
              </div>
            </Field>

            <Field label="Price" required>
              <input
                required
                type="number"
                min="0"
                max="99999999"
                step="0.01"
                value={form.price}
                onChange={(event) => update("price", event.target.value)}
                placeholder="0.00"
              />
            </Field>

            <Field label="Selling Price">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.sellingPrice}
                onChange={(event) => update("sellingPrice", event.target.value)}
                placeholder="0.00"
              />
            </Field>

            <Field label="Discount Percent">
              <input value={`${discountPercent}%`} readOnly />
            </Field>

            <Field label="Stock Quantity" required>
              <input
                required
                type="number"
                min="0"
                max="9999"
                value={form.stock}
                onChange={(event) => update("stock", event.target.value)}
                placeholder="0"
              />
            </Field>

            <Field label="Shipping Charge">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.shippingCharge}
                onChange={(event) =>
                  update("shippingCharge", event.target.value)
                }
                placeholder="0.00"
              />
            </Field>

             <Field label="Status">
             <ToggleSwitch
              label="Status"
              checked={form.isActive}
              onChange={(value) => update("isActive", value)}
            /> 
            </Field>


            <ToggleSwitch
              label="Allow Backorders"
              checked={form.allowBackorders}
              onChange={(value) => update("allowBackorders", value)}
            />

            <ToggleSwitch
              label="Featured Product"
              checked={form.isFeatured}
              onChange={(value) => update("isFeatured", value)}
            />

            <ToggleSwitch
              label="Best Seller"
              checked={form.isBestSeller}
              onChange={(value) => update("isBestSeller", value)}
            />

            <ToggleSwitch
              label="New Arrival"
              checked={form.isNewArrival}
              onChange={(value) => update("isNewArrival", value)}
            />

            <ToggleSwitch
              label="Returnable Product"
              checked={form.isReturnable}
              onChange={(value) => update("isReturnable", value)}
            />
          </div>
        </section>

        <section className="add-card">
          <h2>Product Images</h2>

          <div className="image-upload-grid">
            <label className="dropzone">
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  selectImages(event.target.files);
                  event.target.value = "";
                }}
              />

              <strong>☁</strong>
              <span>Click to upload images</span>

              <small>
                JPG, PNG, WEBP up to 5MB.{" "}
                {images.length || existingImages.length}/{MAX_IMAGES}
              </small>
            </label>

            <div className="image-gallery">
              {!images.length &&
                existingImages.map((image, index) => (
                  <div key={image}>
                    <AppImage
                      src={getImageUrl(image)}
                      alt={`${form.name || "Product"} ${index + 1}`}
                      crossOrigin="anonymous"
                    />

                    {index === 0 && <span>Cover</span>}
                  </div>
                ))}

              {images.map((image, index) => (
                <div key={image.id}>
                  <img src={image.preview} alt={image.file.name} />

                  <button type="button" onClick={() => removeImage(image.id)}>
                    ×
                  </button>

                  {index === 0 && <span>Cover</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </section>
    </form>
  );
};

const Field = ({
  label,
  required,
  className = "",
  children,
}: {
  label: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) => (
  <label className={`add-field ${className}`}>
    <span>
      {label} {required && <b>*</b>}
    </span>

    {children}
  </label>
);

export default ProductAdd;
